import React, { useEffect, useState } from 'react';
import { Box, Stack, TextField, Typography, IconButton, InputAdornment, CircularProgress, Button } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import Iconify from '../components/iconify';
import { auth } from '../config/firebase';
import { passwordRules, confirmPasswordRules } from '../service/passwordValidation';
import {
  AUTH_CARD_MAX_WIDTH,
  authDialogPaperSx,
  authFieldSx,
  authFormSx,
  authPrimaryButtonSx,
} from '../components/modal/modalLayout';

const Schema = Yup.object().shape({
  password: passwordRules,
  confirmPassword: confirmPasswordRules,
});

const readOobCode = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('oobCode') || params.get('oobcode') || '';
};

const kickerSx = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: 'var(--teal-500)',
  textAlign: 'center',
  mb: 1,
};

const titleSx = {
  fontSize: 20,
  fontWeight: 700,
  color: '#111827',
  textAlign: 'center',
};

const subtitleSx = {
  fontSize: 14,
  color: '#6B7280',
  mt: 0.5,
  mb: 2.5,
  textAlign: 'center',
  lineHeight: 1.6,
};

// Branded password form for the link in SendGrid reset / set-password
// emails. Firebase's hosted __/auth/action page is never shown. The
// oobCode in the query is the one-time credential generatePasswordResetLink
// minted; confirmPasswordReset is a client SDK call and does not need
// our backend. The card matches the 440px login / forgot-password modals
// so the three auth surfaces share one grid.
export default function ResetPassword() {
  const [oobCode] = useState(readOobCode);
  const [email, setEmail] = useState(null);
  const [linkState, setLinkState] = useState(oobCode ? 'checking' : 'invalid');
  const [done, setDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!oobCode) return undefined;
    let cancelled = false;
    auth
      .verifyPasswordResetCode(oobCode)
      .then((addr) => {
        if (!cancelled) {
          setEmail(addr);
          setLinkState('ready');
        }
      })
      .catch(() => {
        if (!cancelled) setLinkState('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [oobCode]);

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      await auth.confirmPasswordReset(oobCode, values.password);
      setDone(true);
      toast.success('Your password has been updated.');
    } catch (err) {
      const expired =
        err?.code === 'auth/expired-action-code' || err?.code === 'auth/invalid-action-code';
      toast.error(
        expired
          ? 'This reset link has expired. Request a new one from Sign in.'
          : 'Could not update your password. Please try again.'
      );
      if (expired) setLinkState('invalid');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EditorialPageShell
      title="Reset your password"
      description="Choose a new password for your Kehilat Bnei Torah member portal."
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          px: 2,
          pt: { xs: 14, md: 18 },
          pb: { xs: 8, md: 12 },
        }}
      >
        <Box
          data-testid="reset-password-card"
          sx={{ ...authDialogPaperSx, width: '100%', maxWidth: AUTH_CARD_MAX_WIDTH }}
        >
          <Box sx={{ px: 3, pt: 3, pb: 3 }}>
            <Typography sx={kickerSx}>Member portal</Typography>
            <Typography sx={titleSx}>
              {done ? 'Password updated' : 'Set a new password'}
            </Typography>

            {linkState === 'checking' && (
              <Stack alignItems="center" py={6}>
                <CircularProgress sx={{ color: 'var(--navy-500)' }} />
              </Stack>
            )}

            {linkState === 'invalid' && (
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                <Typography sx={subtitleSx}>
                  This link is expired or has already been used. Request a new one from Sign in —
                  we&apos;ll email it from Kehilat Bnei Torah.
                </Typography>
                <Button
                  component={RouterLink}
                  to="/signin"
                  fullWidth
                  sx={authPrimaryButtonSx}
                >
                  Sign in
                </Button>
              </Stack>
            )}

            {linkState === 'ready' && done && (
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                <Typography sx={subtitleSx}>
                  You can now sign in to the member portal with your new password.
                </Typography>
                <Button
                  component={RouterLink}
                  to="/signin"
                  fullWidth
                  sx={authPrimaryButtonSx}
                >
                  Sign in
                </Button>
              </Stack>
            )}

            {linkState === 'ready' && !done && (
              <>
                <Typography sx={subtitleSx}>
                  {email ? `For ${email}` : 'Choose a password for your member portal.'}
                </Typography>
                <Formik
                  initialValues={{ password: '', confirmPassword: '' }}
                  validationSchema={Schema}
                  onSubmit={onSubmit}
                >
                  {({ handleChange, handleSubmit, touched, errors, values, isSubmitting }) => (
                    <Box component="form" onSubmit={handleSubmit} noValidate sx={authFormSx}>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                          New password
                        </Typography>
                        <TextField
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          value={values.password}
                          onChange={handleChange}
                          error={touched.password && Boolean(errors.password)}
                          helperText={touched.password && errors.password}
                          autoComplete="new-password"
                          inputProps={{ 'data-testid': 'reset-password' }}
                          sx={authFieldSx}
                          fullWidth
                          size="small"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword((v) => !v)}
                                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                                  edge="end"
                                  size="small"
                                  sx={{ width: 36, minWidth: 36, color: '#9CA3AF' }}
                                >
                                  <Iconify icon={showPassword ? 'eva:eye-off-fill' : 'eva:eye-fill'} />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <Box sx={{ mb: 2.5 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                          Confirm password
                        </Typography>
                        <TextField
                          name="confirmPassword"
                          type={showPassword ? 'text' : 'password'}
                          value={values.confirmPassword}
                          onChange={handleChange}
                          error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                          helperText={touched.confirmPassword && errors.confirmPassword}
                          autoComplete="new-password"
                          inputProps={{ 'data-testid': 'reset-confirm' }}
                          sx={authFieldSx}
                          fullWidth
                          size="small"
                        />
                      </Box>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        fullWidth
                        sx={authPrimaryButtonSx}
                      >
                        {isSubmitting ? 'Saving…' : 'Save new password'}
                      </Button>
                    </Box>
                  )}
                </Formik>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </EditorialPageShell>
  );
}
